// v640.9: Achievement Gallery — full catalog grouped by category, with
// progress bars for counter-based achievements and unlock dates.
// v642.10: inline TooltipBadge injection — when an achievement
// description references a known game system (rested XP, world tier,
// CHA discount, market commission, patron perks) we append the matching
// (?) badge so players can read the rules without leaving the panel.
import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Social.css';
import TooltipBadge from './shared/TooltipBadge';

// Each entry: regex to detect, and the systemTooltips key. Order matters —
// the first match wins so a description that mentions both 'rested' and
// 'world tier' would get one badge per distinct system, not duplicates.
const TOOLTIP_TRIGGERS = [
  { re: /rested\s*xp|rested[- ]bonus/i,                  id: 'xp.rested' },
  { re: /world\s*tier|tier\s*\d|on tier/i,               id: 'world.tier' },
  { re: /charisma|cha\s*discount|shop\s*discount/i,      id: 'shop.cha-discount' },
  { re: /market\s*commission|auction\s*fee|listing\s*fee/i, id: 'market.commission' },
  { re: /patron|crown\s*shop|supporter\s*perks?/i,       id: 'patron.perks' },
];

// Returns the set of tooltip ids that should be appended to a given
// description. We append (rather than inject inline) so we don't have to
// parse and rebuild the description text — this keeps localisation
// straightforward and avoids breaking sentence flow.
const tooltipsFor = (text) => {
  if (!text || typeof text !== 'string') return [];
  const ids = [];
  for (const { re, id } of TOOLTIP_TRIGGERS) {
    if (re.test(text) && !ids.includes(id)) ids.push(id);
  }
  return ids;
};

const CATEGORY_META = {
  combat:      { label: '⚔️ Combat',      color: '#e74c3c' },
  exploration: { label: '🗺️ Exploration', color: '#10b981' },
  progression: { label: '📈 Progression', color: '#c9a84c' },
  economy:     { label: '💰 Economy',     color: '#f59e0b' },
  bounties:    { label: '🎯 Bounties',    color: '#f97316' },
  collection:  { label: '🃏 Collection',  color: '#8b5cf6' },
  misc:        { label: '✨ Misc',        color: '#6b7280' },
};

const AchievementsModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'earned' | 'locked'

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/player/achievements', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load');
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const categoryOrder = useMemo(() => {
    if (!data?.categories) return [];
    const known = ['combat', 'exploration', 'progression', 'economy', 'bounties', 'collection'];
    const present = Object.keys(data.categories);
    return [
      ...known.filter(c => present.includes(c)),
      ...present.filter(c => !known.includes(c)),
    ];
  }, [data]);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(); } catch { return null; }
  };

  const matchesFilter = (a) => {
    if (filter === 'earned') return a.earned;
    if (filter === 'locked') return !a.earned;
    return true;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, padding: 18, maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#c9a84c', fontSize: 20 }}>
            🏆 Achievements
            {data && <span style={{ fontSize: 13, color: '#888', marginLeft: 10, fontWeight: 'normal' }}>{data.earned} / {data.total}</span>}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            title="Close (Esc)"
          >×</button>
        </div>

        {data && data.total > 0 && (
          <>
            <div style={{
              height: 6, background: 'rgba(0,0,0,0.4)', borderRadius: 3, marginBottom: 12, overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${Math.round((data.earned / data.total) * 100)}%`,
                background: 'linear-gradient(90deg, #c9a84c, #f59e0b)',
                transition: 'width 300ms ease',
              }} />
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[
                { id: 'all', label: 'All' },
                { id: 'earned', label: 'Earned' },
                { id: 'locked', label: 'Locked' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    background: filter === f.id ? 'rgba(201, 168, 76, 0.25)' : 'rgba(20,20,28,0.6)',
                    border: `1px solid ${filter === f.id ? '#c9a84c' : '#444'}`,
                    color: filter === f.id ? '#c9a84c' : '#aaa',
                    padding: '4px 12px',
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >{f.label}</button>
              ))}
            </div>
          </>
        )}

        {loading && <div style={{ color: '#999', padding: '20px 0', textAlign: 'center' }}>Loading…</div>}
        {error && <div style={{ color: '#e74c3c', padding: '20px 0', textAlign: 'center' }}>{error}</div>}

        {data && categoryOrder.map(cat => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.misc;
          const items = (data.categories[cat] || []).filter(matchesFilter);
          if (!items.length) return null;
          const earnedInCat = (data.categories[cat] || []).filter(a => a.earned).length;
          const totalInCat = (data.categories[cat] || []).length;
          return (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{
                color: meta.color,
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 6,
                paddingBottom: 4,
                borderBottom: `1px solid ${meta.color}33`,
              }}>
                {meta.label} <span style={{ color: '#666', fontWeight: 'normal' }}>· {earnedInCat}/{totalInCat}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {items.map(a => {
                  const hasProgress = typeof a.target === 'number' && !a.earned;
                  const pct = hasProgress ? Math.min(100, Math.round((a.progress / a.target) * 100)) : 0;
                  return (
                    <div
                      key={a.id}
                      title={a.earned && a.unlockedAt ? `Unlocked ${fmtDate(a.unlockedAt)}` : a.description}
                      style={{
                        background: a.earned ? 'rgba(201, 168, 76, 0.08)' : 'rgba(20, 20, 28, 0.6)',
                        border: `1px solid ${a.earned ? meta.color : '#333'}`,
                        borderRadius: 6,
                        padding: '8px 10px',
                        opacity: a.earned ? 1 : 0.7,
                        display: 'flex',
                        gap: 8,
                        alignItems: 'flex-start',
                      }}
                    >
                      <div style={{
                        fontSize: 22,
                        filter: a.earned ? 'none' : 'grayscale(0.8) brightness(0.6)',
                        flexShrink: 0,
                      }}>{a.icon || '🏆'}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 12,
                          color: a.earned ? '#c9a84c' : '#999',
                          fontWeight: 600,
                          marginBottom: 2,
                        }}>{a.name}</div>
                        <div style={{
                          fontSize: 10,
                          color: '#888',
                          lineHeight: 1.3,
                          marginBottom: hasProgress ? 4 : 0,
                        }}>
                          {a.description}
                          {tooltipsFor(a.description).map(tid => (
                            <TooltipBadge key={tid} id={tid} size={11} />
                          ))}
                        </div>
                        {hasProgress && (
                          <>
                            <div style={{ height: 4, background: 'rgba(0,0,0,0.4)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: `${pct}%`,
                                background: meta.color,
                              }} />
                            </div>
                            <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>
                              {a.progress} / {a.target}
                            </div>
                          </>
                        )}
                        {a.earned && a.unlockedAt && (
                          <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>
                            ✓ {fmtDate(a.unlockedAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AchievementsModal;
